import React, { useEffect, useState } from "react";
import { withRouter, RouteComponentProps } from "react-router-dom";
import { generatePath } from "react-router";
import { Button } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import { observer } from "mobx-react-lite";

import Container from "components/Layout/Container/Container";
import Header from "components/Layout/Header/Header";
import Page from "components/Layout/Page/Page";
import PublishButton from "components/PublishButton/PublishButton";
import Placeholder from "components/Placeholder/Placeholder";
import ROUTES from "navigation/routes";
import withGlobalPopup, {
  GlobalPopupProps,
} from "components/Popup/withGlobalPopup";
import {
  withGlobalSnackbar,
  SnackbarContextProps,
} from "services/snackbar/snackbarContext";
import { PageControl } from "./styles";
import WorkoutSequenceCard from "./WorkoutSequenceCard/WorkoutSequenceCard";

import SequenceForm from "./SequenceForm/SequenceForm";
import { SNACKBAR_TYPES } from "components/Snackbar/Snackbar";
import WorkoutSequenceStore from "stores/WorkoutSequenceStore";
import { getMessage } from "utils/messages";

type Props = GlobalPopupProps & SnackbarContextProps & RouteComponentProps & {};

interface WorkoutSequence {
  id: number;
  title: string;
  showFirstWeekly: boolean;
}

const InAppWorkoutSequences: React.FC<Props> = ({
  openPopup,
  setMessage,
  history,
}) => {
  const [sequences, setSequences] = useState<WorkoutSequence[]>([]);

  const fetchSequences = async () => {
    try {
      await WorkoutSequenceStore.fetchAllSequences();
      setSequences(WorkoutSequenceStore.sequencesList);
    } catch (error) {
      console.error("Failed to fetch workout sequences:", error);
    }
  };

  useEffect(() => {
    fetchSequences();
  }, []);

  const onManageSequence = (initialValues?: any) => {
    openPopup({
      key: "ManageSequence",
      canCloseByOuterClick: false,
      withoutExitButton: true,
      render: ({ close }) => (
        <SequenceForm
          initialValues={initialValues}
          onSubmit={onManageSequenceSubmit(close)}
          onClose={close}
        />
      ),
    });
  };

  const onManageSequenceSubmit = (close: () => void) => {
    return async (value: {
      title: string;
      id: number;
      showFirstWeekly: boolean;
    }) => {
      const { id, title, showFirstWeekly } = value;
      if (title) {
        try {
          if (id) {
            await WorkoutSequenceStore.updateWorkoutSequence(
              id,
              title,
              showFirstWeekly,
            );
          } else {
            await WorkoutSequenceStore.createWorkoutSequence(
              title,
              showFirstWeekly,
            );
          }
          close();
          fetchSequences();
        } catch (error) {
          setMessage(getMessage(error?.message), SNACKBAR_TYPES.ERROR);
        }
      }
    };
  };

  const onAddSequence = () => {
    onManageSequence();
  };

  const onEditSequence = (sequence: WorkoutSequence) => {
    onManageSequence({
      title: sequence.title,
      id: sequence.id,
      showFirstWeekly: !sequence.showFirstWeekly,
    });
  };

  const onDetailsClick = (workoutSequence: any) => {
    history.push({
      pathname: generatePath(ROUTES.IN_APP_STUDIO_WORKOUT_SEQUENCE_DETAILS, {
        id: workoutSequence.id,
      }),
      state: {
        editMode: false,
      },
    });
  };

  return (
    <Container header={<Header controls={<PublishButton />} />}>
      <Page
        isLoading={WorkoutSequenceStore.isLoading}
        title="Workout Sequences"
        controls={
          <PageControl>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={onAddSequence}
            >
              Add Workout Sequence
            </Button>
          </PageControl>
        }
      >
        {sequences.length === 0 ? (
          <Placeholder
            height={280}
            width={280}
            text={"No sequences added yet."}
          />
        ) : (
          sequences.map(sequence => (
            <WorkoutSequenceCard
              key={sequence.id}
              title={sequence.title}
              onDetailsClick={() => onDetailsClick(sequence)}
              onEdit={() => onEditSequence(sequence)}
            />
          ))
        )}
      </Page>
    </Container>
  );
};

export default withGlobalPopup(
  withGlobalSnackbar(withRouter(observer(InAppWorkoutSequences))),
);
